import { XCircleIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { isAfter, isBefore, isValid, subYears } from 'date-fns';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatBirthDate } from '../../../components/util';
import { useApplicationStore } from './applicationStore';

const ExistingClient = () => {
	const { setExistingClient, setStatus, resetClient } = useApplicationStore();
	const [username, setUsername] = useState('');
	const [lname, setLName] = useState('');
	const [dob, setDob] = useState('');
	const navigate = useNavigate();

	const usernameQuery = useQuery({
		queryKey: ['usernames', username],
		queryFn: async () => {
			const { data } = await axios.get('/coord_agencies/findUserByUsername', {
				params: {
					username,
				},
			});
			return data;
		},
		enabled: false,
	});
	const nameAndDobQuery = useQuery({
		queryKey: ['client', lname, dob],
		queryFn: async () => {
			const { data } = await axios.get(
				'/coord_agencies/find-client-by-lname-dob',
				{
					params: {
						lname,
						dob,
					},
				},
			);
			return data;
		},
		enabled: false,
	});

	const handleUsernameSearch = (event) => {
		event.preventDefault();
		usernameQuery.refetch();
		resetClient();
	};

	const handleNameAndDobSearch = (event) => {
		event.preventDefault();
		nameAndDobQuery.refetch();
		resetClient();
	};

	const handleExistingClientChosen = (client) => {
		setExistingClient(client);
		setStatus(undefined);
		navigate('/newApplication/secure');
	};

	let queryData;
	if (usernameQuery.isFetched || nameAndDobQuery.isFetched) {
		if (usernameQuery.dataUpdatedAt > nameAndDobQuery.dataUpdatedAt) {
			queryData = usernameQuery.data;
		} else if (nameAndDobQuery.dataUpdatedAt > usernameQuery.dataUpdatedAt) {
			queryData = nameAndDobQuery.data;
		}
	}

	const dobDate = new Date(dob);
	const isValidDOB =
		isValid(dobDate) &&
		isBefore(dobDate, new Date()) &&
		isAfter(dobDate, subYears(new Date(), 150));

	return (
		<>
			<input type="hidden" name="step" value="client" />
			<h1>Existing Profile</h1>
			<p>
				<em>
					Find client by Username or Last Name and Date of Birth. Only exact
					matches will be returned.
				</em>
			</p>
			<div className="grid items-start grid-cols-2 gap-4 mt-4 mb-8">
				<div className="border border-gray-200 rounded-lg">
					<div className="grid grid-cols-1 gap-6 px-8 pt-4 pb-6">
						<div className="form-control">
							<label htmlFor="username" className="label">
								Username
							</label>
							<input
								type="text"
								name="username"
								id="username"
								autoComplete="off"
								className="w-full input input-bordered"
								onChange={({ target }) => setUsername(target.value)}
								value={username}
								minLength={2}
							/>
						</div>
						<div>
							<button
								className="btn-block btn btn-primary"
								onClick={handleUsernameSearch}
								disabled={username.length === 0}
							>
								Search
							</button>
						</div>
					</div>
				</div>
				<div className="border border-gray-200 rounded-lg">
					<div className="grid grid-cols-1 gap-6 px-8 pt-4 pb-6">
						<div className="form-contro">
							<label htmlFor="lname" className="label">
								Last Name
							</label>
							<input
								type="text"
								name="lname"
								id="lname"
								autoComplete="off"
								className="w-full input input-bordered"
								onChange={({ target }) => setLName(target.value)}
								value={lname}
							/>
						</div>
						<div className="form-control">
							<label htmlFor="dob" className="label">
								Date of Birth
							</label>
							<input
								id="dob"
								name="dob"
								type="date"
								autoComplete="off"
								className="input input-bordered"
								onChange={({ target }) => setDob(target.value)}
								value={dob}
							/>
						</div>
						<div>
							<button
								className="btn-block btn btn-primary"
								onClick={handleNameAndDobSearch}
								disabled={lname.length === 0 || !isValidDOB}
							>
								Search
							</button>
						</div>
					</div>
				</div>
			</div>
			{queryData && (
				<table className="table mb-8">
					<thead>
						<tr>
							<th></th>
							<th>Last Name</th>
							<th>First Name</th>
							<th>DOB</th>
							<th>Mobile</th>
							<th>Email</th>
						</tr>
					</thead>
					<tbody>
						{queryData && queryData.length === 0 && (
							<tr>
								<td colSpan={5}>No results found</td>
							</tr>
						)}
						{queryData &&
							queryData.map((client) => (
								<tr key={`${client.id}`}>
									<td>
										{!isEmpty(client.status) && isEmpty(client.last_4_ssn) && (
											<div
												className="tooltip tooltip-right"
												data-tip="No Tandem profile"
											>
												<XCircleIcon className="w-5 h-5 text-error" />
											</div>
										)}
									</td>
									<td>{client.name_last}</td>
									<td>{client.name_first}</td>
									<td>{formatBirthDate(client.birth_dttm)}</td>
									<td>{client.phone_mobile}</td>
									<td>{client.email}</td>
									<td>
										<button
											className="btn btn-primary btn-sm"
											type="submit"
											onClick={() => handleExistingClientChosen(client)}
										>
											Choose
										</button>
									</td>
								</tr>
							))}
					</tbody>
				</table>
			)}
		</>
	);
};

export default ExistingClient;
